import { z } from 'zod';
import { configFromEnv } from '@utils/config';
import { saveComponentEntry } from '../../api/request.save-component-entry.js';

const saveEntryPath = configFromEnv.asString("SAVE_ENTRY_PATH", {
  schema: z.string().optional()
});
const autoSaveDebounceMs = 650;
const cache = new Map();

// CALLBACK: external method that is triggered on-demand and requests that
// new data is stored in the UserData API for the user's request
//
// in practice, the actual save request is debounced and deferred until any
// currently active request to save data has been carried out
async function saveComponentEntryRequest(user, entry) {
  if (!user) {
    return entry;
  }
  const self = getSelf(entry.entryKey);
  if (self.saveTimeoutId !== undefined) {
    window.clearTimeout(self.saveTimeoutId);
  }
  if (!self.boundUnloadListener && typeof window !== "undefined") {
    // CALLBACK: triggered if the user attempts to leave the current page, and
    // warns him in case there are any unsaved changes pending
    const handleBeforeUnload = evt => {
      if (self.saveTimeoutId === undefined && self.savePromise === undefined) {
        return;
      }
      evt.preventDefault();
      evt.returnValue = "Dine ændringer er endnu ikke gemt, er du sikker på du vil forlade denne side?";
      return "Dine ændringer er endnu ikke gemt, er du sikker på du vil forlade denne side?";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
  }
  return new Promise((resolve, reject) => {
    self.saveTimeoutId = window.setTimeout(() => {
      self.saveTimeoutId = undefined;
      if (self.savePromise !== undefined) {
        // if a save promise is currently running, then simply update the next
        // desired entry data to be saved so it will be dispatched once the
        // current request finishes
        self.nextEntryData = entry.data;
        self.savePromise.then(res => resolve(res)).catch(reject);
        return;
      }

      // ... otherwise perform the save request now
      performSave(user, entry).then(res => resolve(res)).catch(reject);
    }, autoSaveDebounceMs);
  });
}

// CALLBACK: actual implementation that saves data through the desired
// gateway (handling of debouncing and nonconcurrency has already been
// handled, so here we can dispatch requests immediately)
function performSave(user, entry) {
  const self = getSelf(entry.entryKey);
  self.savePromise = new Promise((resolve, reject) => {
    (saveEntryPath.dangerouslyAssertSyncValue() ? saveThroughNextApi(entry) : saveComponentEntry(user, entry)).then(entry => {
      if (self.nextEntryData) {
        // if data has changed while we were waiting for this request to
        // complete, then instantly begin saving the next data set
        performSave(user, {
          ...entry,
          data: self.nextEntryData
        }).then(entry => {
          resolve(entry);
        }).catch(reject);
        self.nextEntryData = undefined;
      } else {
        resolve(entry);
        self.savePromise = undefined;
      }
    }).catch(err => {
      reject(err);
      self.savePromise = undefined;
    });
  });
  return self.savePromise;
}

// specific integration to use for saving the component entry using a Next.js
// api integration from this package
async function saveThroughNextApi(entry) {
  const res = await fetch((await saveEntryPath.get()) ?? "", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      entry
    }),
    credentials: "include"
  });
  if (!res.ok) {
    throw new Error("saveComponentEntry.saveThroughNextApi(): Failed to save component");
  }
  return await res.json();
}
function getSelf(entryKey) {
  let self = cache.get(entryKey);
  if (self) {
    return self;
  }
  self = {
    nextEntryData: undefined,
    saveTimeoutId: undefined,
    savePromise: undefined,
    boundUnloadListener: false
  };
  cache.set(entryKey, self);
  return self;
}

export { saveComponentEntryRequest };
//# sourceMappingURL=request.save.js.map
